import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import { z } from 'zod';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';

const signupSchema = z.object({
  email: z.string()
    .trim()
    .min(1, 'El email es requerido')
    .email('Email inválido')
    .max(255, 'El email no puede exceder 255 caracteres'),
  password: z.string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .max(128, 'La contraseña no puede exceder 128 caracteres')
    .regex(/[A-Z]/, 'Debe contener al menos una letra mayúscula')
    .regex(/[0-9]/, 'Debe contener al menos un número'),
  fullName: z.string()
    .trim()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres')
    .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'El nombre solo puede contener letras, espacios, guiones y apóstrofes'),
  tag: z.string()
    .trim()
    .max(20, 'El tag no puede exceder 20 caracteres')
    .regex(/^[a-zA-Z0-9-_]*$/, 'El tag solo puede contener letras, números, guiones y guiones bajos')
    .optional()
    .or(z.literal('')),
  employeeId: z.string()
    .trim()
    .max(50, 'El ID de empleado no puede exceder 50 caracteres')
    .regex(/^[a-zA-Z0-9-_]*$/, 'El ID de empleado solo puede contener letras, números, guiones y guiones bajos')
    .optional()
    .or(z.literal('')),
  operatorArea: z.enum(['corte_plotter', 'laminador', 'taller', 'generico'], {
    required_error: 'Debe seleccionar un área de trabajo',
  }),
});

const RegisterUser = () => {
  const { signUp, userRole } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [tag, setTag] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [operatorArea, setOperatorArea] = useState('');

  // Check if user has permission
  if (userRole !== 'admin' && userRole !== 'supervisor') {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-foreground">Acceso Denegado</h2>
          <p className="text-muted-foreground mt-2">
            Solo administradores y supervisores pueden registrar nuevos usuarios.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      const validated = signupSchema.parse({
        email,
        password,
        fullName,
        tag: tag || undefined,
        employeeId: employeeId || undefined,
        operatorArea,
      });
      
      setLoading(true);
      const { error } = await signUp(
        validated.email,
        validated.password,
        validated.fullName,
        validated.tag,
        validated.employeeId,
        validated.operatorArea
      );

      if (error) {
        toast.error('Error al registrar usuario: ' + error.message);
      } else {
        toast.success('Usuario registrado exitosamente');
        // Clear form on success
        setEmail('');
        setPassword('');
        setFullName('');
        setTag('');
        setEmployeeId('');
        setOperatorArea('');
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: any = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0]] = err.message;
          }
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <UserPlus className="w-8 h-8" />
            Registrar Nuevo Usuario
          </h1>
          <p className="text-muted-foreground mt-2">
            Crear una cuenta nueva para un operario
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Nombre Completo *</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Juan Pérez"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
              {errors.fullName && (
                <p className="text-xs text-destructive">{errors.fullName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="usuario@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Mínimo 6 caracteres, 1 mayúscula, 1 número"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {errors.password && (
                <p className="text-xs text-destructive">{errors.password}</p>
              )}
              <p className="text-xs text-muted-foreground">
                La contraseña debe tener al menos 6 caracteres, incluir una letra mayúscula y un número
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="employeeId">ID de Empleado (Opcional)</Label>
              <Input
                id="employeeId"
                type="text"
                placeholder="EMP-001"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              />
              {errors.employeeId && (
                <p className="text-xs text-destructive">{errors.employeeId}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tag">Tag/Identificador Corto (Opcional)</Label>
              <Input
                id="tag"
                type="text"
                placeholder="jperez"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                maxLength={20}
              />
              {errors.tag && (
                <p className="text-xs text-destructive">{errors.tag}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Identificador corto para el operario (máx. 20 caracteres)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="operatorArea">Área de Trabajo *</Label>
              <Select value={operatorArea} onValueChange={setOperatorArea} required>
                <SelectTrigger id="operatorArea">
                  <SelectValue placeholder={t.auth.selectArea} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="corte_plotter">{t.auth.areas.corte_plotter}</SelectItem>
                  <SelectItem value="laminador">{t.auth.areas.laminador}</SelectItem>
                  <SelectItem value="taller">{t.auth.areas.taller}</SelectItem>
                  <SelectItem value="generico">{t.auth.areas.generico}</SelectItem>
                </SelectContent>
              </Select>
              {errors.operatorArea && (
                <p className="text-xs text-destructive">{errors.operatorArea}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Registrando...' : 'Registrar Usuario'}
            </Button>
          </form>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default RegisterUser;
